<template>
    <app-layout>
      <Head title="Scheduled"/>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                My Subscription
            </h2>
        </template>

        <PanelSlot
            v-if="activePlan"
            title="Current Plan"
            subtitle="Your current subscription details."
            :topBorder="false"
        >
            <div class="text-18px font-semibold">
                <div v-if="$page.props.paused">
                    <div v-if="$page.props.grace_period">
                        Your account is currently paused.
                    </div>
                    <div v-else>Your account is currently paused for $5 per month. 
                        
                        <div class="text-sm font-normal text-gray-600">Upgrade your plan when you're ready to start sending messages again.
                        </div>
                    </div>
                </div>
                <div v-else>
                    {{ activePlan.limit }} credits per {{ activePlan.interval }} at ${{ (activePlan.price / 100).toLocaleString('en-us') }} per {{ activePlan.interval }}.
                    <br />
                    <div v-if="additionalUsers > 0 && teamMemberPlan" class="text-sm font-normal text-gray-600 mt-1">
                        {{ additionalUsers }} additional {{ additionalUsers === 1 ? 'user' : 'users' }} at ${{ (teamMemberPlan.price / 100).toLocaleString('en-us') }}/{{ activePlan.interval }} each.
                        <Link :href="route('team.index')" class="text-blue-600 hover:text-blue-800 underline">Manage users →</Link>
                    </div>
                </div>
                
                <span v-if="!$page.props.grace_period" class="text-sm font-normal">
                    <template v-if="scheduledPlanChangeDate">
                        Plan changes to {{ scheduledPlanDetails }} on {{ scheduledPlanChangeDate }}
                    </template>
                    <template v-else-if="$page.props.pause_scheduled">
                        Account pauses on {{ pauseAt }} for $5 per month
                    </template>
                    <template v-else>
                        Next payment on {{ nextBillingDate }}
                    </template>
                </span>
                
                <div v-if="$page.props.grace_period" class="text-red-600 border border-red-600 text-16px px-4 py-3 mt-2">
                    Your subscription has been set to cancel on {{ nextBillingDate }}. <br /><br />
                    <div class="text-gray-800 ">
                        Made a mistake? <span @click="resumeSubscription" class='underline cursor-pointer text-blue-600'>Turn back on renewal to keep your current subscription.</span>
                    </div>
                </div>

                <div v-if="$page.props.pause_scheduled" class="text-red-600 border border-red-600 text-16px px-4 py-3 mt-2">
                    Your account is scheduled to be paused on {{ pauseAt }}. You can <span class="underline cursor-pointer" @click="cancelPause">cancel the pause</span> to stay on your current plan.
                </div>

                <div v-if="scheduledPlanChangeDate" class="text-orange-600 border border-orange-600 text-16px px-4 py-3 mt-2">
                    Your plan is scheduled to change. You can <span class="underline cursor-pointer" @click="cancelScheduledPlanChange">cancel this change</span> to stay on your current plan.
                </div>
                
            </div>
            <div>

                <a :href="route('billing.portal')" class="w-full text-center">
                    <jet-button secondary class="mt-6  mr-8">
                        Manage Billing
                    </jet-button>
                </a>

                <Link :href="route('subscriptions.index')" class="w-full text-center">
                    <jet-button class="mt-6">
                        Upgrade Plan
                    </jet-button>
                </Link>
            </div>
        </PanelSlot>

        <PanelSlot
            v-if="activePlan"
            title="Cancel Subscription"
            subtitle="Cancel your subscription or pause account"
        >
            <div class="text-gray-600 mb-6" v-if="!$page.props.grace_period">
                Cancelling your subscription means you will lose your current phone number and A2P registration status. 
                <div v-if="!$page.props.paused && !$page.props.pause_scheduled">
                    To keep your account active, current phone number and registration status, pause your account for $5 / month.
                </div>
            </div>
            
            <div class="text-gray-600 mb-6 " v-if="$page.props.grace_period">
                Your subscription is scheduled to be cancelled on {{ nextBillingDate }}. <br /><br />

                You will lose:
                <ul class="list-disc ml-6">
                    <li>Your current phone number</li>
                    <li>Your A2P 10DLC registration status</li>
                    <li>Ability to send and receive messages</li>
                </ul>
                <br />
                <span v-if="!$page.props.paused && !$page.props.pause_scheduled">
                    You can pause your account for $5 / month to keep your current phone number and registration status.
                </span>
            </div>
            
            <div class="flex items-center">
                <jet-button class="mr-8" :danger="true" type="button" @click.native="showCancelAccountConfirmModal=true" 
                    v-if="!$page.props.grace_period"
                >
                    Cancel Subscription
                </jet-button>

                <jet-button class="text-gray-600" :text="!$page.props.grace_period" type="button" @click.native="showPauseAccountConfirmationModal=true" :loading="pauseLoading"
                    v-if="!$page.props.auth.user.organization.pause_at"
                >
                    Pause Account
                </jet-button>
            </div>
        </PanelSlot>

        <CancelAccountConfirm v-if="showCancelAccountConfirmModal" 
            @close="showCancelAccountConfirmModal = false" 
            @pause="showPauseAccountConfirmationModal = true"
            @continue-cancellation="showCancelAccountConfirmModalFinal = true"
        />
        <CancelAccountConfirmFinal v-if="showCancelAccountConfirmModalFinal" 
            @close="showCancelAccountConfirmModalFinal = false"
            @cancel="cancelSubscription"
        />
        <PauseAccountConfirmation 
            v-if="showPauseAccountConfirmationModal" 
            @close="showPauseAccountConfirmationModal = false"
            @pause="pauseAccount"
        />

    </app-layout>
</template>

<script>
import axios from "axios";
import PanelSlot from "@/components/PanelSlot";
import CancelAccountConfirm from "@/components/Modals/SubscriptionManagement/CancelAccountConfirm.vue";
import CancelAccountConfirmFinal from "@/components/Modals/SubscriptionManagement/CancelAccountConfirmFinal.vue";
import PauseAccountConfirmation from "@/components/Modals/SubscriptionManagement/PauseAccountConfirmation.vue";
import moment from 'moment';
export default {
    components: {
        PanelSlot, CancelAccountConfirm, CancelAccountConfirmFinal, PauseAccountConfirmation
   },
    props: {
        
    },
    data() {
        return {
            showCancelAccountConfirmModal: false,
            showCancelAccountConfirmModalFinal: false,
            showPauseAccountConfirmationModal: false,
            reasons: {},
            pauseLoading: false,
            attemptedCancel: false,
        };
    },
    computed: {
        activePlan() {
            return this.$page.props.active_plan;
        },
        additionalUsers() {
            return this.$page.props.additional_users || 0;
        },
        teamMemberPlan() {
            return this.$page.props.team_member_plan;
        },
        pauseAt () {
            return moment(this.$page.props.auth.user.organization.pause_at).format('MMMM D, YYYY');
        },
        scheduledPlanChangeDate() {
            const changeAt = this.$page.props.auth.user.organization.scheduled_plan_change_at;
            if (changeAt) {
                return moment(changeAt).format('MMMM D, YYYY');
            }
            return null;
        },
        scheduledPlanDetails() {
            if (this.$page.props.scheduled_plan_details) {
                const plan = this.$page.props.scheduled_plan_details;
                return `${plan.limit.toLocaleString()} credits per ${plan.interval} at $${(plan.price / 100).toLocaleString('en-us')} per ${plan.interval}`;
            }
            return 'your new plan';
        },
        nextBillingDate() {
            return moment.unix(this.$page.props.current_plan_end_date).format('MMMM D, YYYY');
        }
    },
    methods: {
        pauseAccount() {
            this.showPauseAccountConfirmationModal = false;
            this.pauseLoading = true;
            axios.post(route('subscriptions.pause')).then(response => {
                this.$toasted.show(response.data.message, {
                    position: "bottom-right",
                    type: response.data.status,
                    duration: 3000
                });
                this.pauseLoading = false;
                if (response.data.status == "success") {
                    
                    setTimeout(() => {
                        this.$inertia.reload();
                    }, 3000);
                    return;
                }

            });
        },
        cancelPause() {
            if (this.attemptedCancel) {
                return;
            }
            this.attemptedCancel = true;

            axios.post(route('subscriptions.cancel-pause')).then(response => {
                this.$toasted.show(response.data.message, {
                    position: "bottom-right",
                    type: response.data.status,
                    duration: 3000
                });

                if (response.data.status == "success") {
                    setTimeout(() => {
                        this.$inertia.reload();
                    }, 3000);
                    return;
                }
            });
        },
        cancelSubscription(reasons) {
            axios.post(route('subscriptions.cancel'), {reasons}).then(response => {
                this.$toasted.show(response.data.message, {
                    position: "bottom-right",
                    type: response.data.status,
                    duration: 3000
                });

                if (response.data.status == "success") {
                    setTimeout(() => {
                        this.$inertia.reload();
                    }, 3000);
                }
                this.showCancelAccountConfirmModalFinal = false;
            });
        },
        resumeSubscription() {
            axios.post(route('subscriptions.resume')).then(response => {
                this.$toasted.show(response.data.message, {
                    position: "bottom-right",
                    type: response.data.status,
                    duration: 3000
                });

                if (response.data.status == "success") {
                    setTimeout(() => {
                        this.$inertia.reload();
                    }, 3000);
                }
            });
        },
        cancelScheduledPlanChange() {
            if (this.attemptedCancel) {
                return;
            }
            this.attemptedCancel = true;

            axios.post(route('subscriptions.cancel-scheduled-change')).then(response => {
                this.$toasted.show(response.data.message, {
                    position: "bottom-right",
                    type: response.data.status,
                    duration: 3000
                });

                if (response.data.status == "success") {
                    setTimeout(() => {
                        this.$inertia.reload();
                    }, 3000);
                    return;
                }
            }).finally(() => {
                this.attemptedCancel = false;
            });
        }
    }
};
</script>
